import { createSelector } from "reselect";
import { createAction } from "redux-actions";
import {
  pipe,
  path,
  merge,
  groupBy,
  prop,
  filter,
  propSatisfies,
  isNil,
  isEmpty,
  includes,
  __,
  propEq,
  reduce,
  concat,
  map,
  pathOr,
  pick,
  curryN,
  reject,
  unnest,
  values,
  over,
  lensProp,
  inc,
  find,
  union,
  either,
  sort,
  comparator,
  mergeWith,
  omit,
  lt,
  toLower
} from "ramda";

import { objFromKeys } from "~/utils";
import {
  relativePageToAbsoluteNumerical,
  absolutePageToRelativeNumerical
} from "~/utils/page";
import { MATERIAL_TYPES, NON_ASSIGNED_PAGE } from "~/constants/materials";

import { selectMarkedMaterialsByPages } from "~/store/selectors/dls";

import {
  selectIsPresentationActive,
  selectIsPresentationMaterialVisible
} from "~/store/ducks/user/uistate";

// Takes a list of predicates and applies them to the second argument
// e.g. variadicEither([x => x.length > 4, x => x.includes("A")])("BBBBB") => true
const variadicEither = ([head, ...tail]) => reduce(either, head, tail);

const hasEmptyPositions = propSatisfies(either(isNil, isEmpty), "positions");

const xPositionComparator = comparator((a, b) =>
  lt(path(["position", "x"], a), path(["position", "x"], b))
);

const yPositionComparator = comparator((a, b) =>
  lt(path(["position", "y"], a), path(["position", "y"], b))
);

const titleComparator = comparator((a, b) =>
  lt(toLower(pathOr("", ["title"], a)), toLower(pathOr("", ["title"], b)))
);

const typeComparator = comparator((a, b) =>
  lt(propEq("type", "material", a), propEq("type", "material", b))
);

export const ADD_MATERIAL = "APP@MATERIAL::ADD";
export const ADD_MATERIAL_MULTIPLE = "APP@MATERIAL::ADD_MULTIPLE";
export const REMOVE_MATERIAL = "APP@MATERIAL::REMOVE";
export const REMOVE_MATERIAL_POSITION = "APP@MATERIAL::REMOVE_POSITION";
export const UPDATE_MATERIAL = "APP@MATERIAL::UPDATE";
export const UPDATE_MATERIAL_POSITIONS = "APP@MATERIAL::UPDATE_POSITIONS";
export const MOVE_MATERIAL = "APP@MATERIAL::MOVE";

export const addMaterial = createAction(ADD_MATERIAL);
export const addAllMaterials = createAction(ADD_MATERIAL_MULTIPLE);
export const removeMaterial = createAction(REMOVE_MATERIAL);
export const removeMaterialPosition = createAction(REMOVE_MATERIAL_POSITION);
export const updateMaterial = createAction(UPDATE_MATERIAL);
export const updateMaterialPositions = createAction(
  UPDATE_MATERIAL_POSITIONS,
  (materialId, newPosition) => ({
    materialId,
    newPosition
  })
);

export const moveMaterial = createAction(
  MOVE_MATERIAL,
  (materialId, positionId, position) => ({
    materialId,
    positionId,
    position
  })
);

// export const moveMaterial = (materialId, positionId, newPosition) => (
//   dispatch,
//   getState
// ) => {
//   const state = getState();
//   const materials = selectMaterials(state);
//   let material = materials.find(material => {
//     return material._id === materialId;
//   });

//   if (material) {
//     const positions = material.positions.map(position => {
//       if (position._id === positionId) {
//         return { ...position, ...newPosition };
//       }

//       return position;
//     });

//     material = { ...material, positions };

//     dispatch(updateMaterial(material));
//   }
// };

const getMaterials = path(["application", "materials"]);

export const selectMaterials = createSelector(getMaterials, materials =>
  prop("materials")(materials).map(material => {
    material.positions = material.positions || [];
    return {
      ...material,
      positions: material.positions.map(position => ({
        ...position,
        page: material.withOffset
          ? relativePageToAbsoluteNumerical(position.page)
          : position.page
      }))
    };
  })
);

export const selectMaterialsByType = createSelector(
  selectMaterials,
  pipe(groupBy(prop("materialType")), merge(objFromKeys(MATERIAL_TYPES, [])))
);

export const selectPagelessMaterials = createSelector(
  selectMaterials,
  filter(hasEmptyPositions)
);

export const selectFilteredPagelessMaterials = activeItems =>
  createSelector(
    selectPagelessMaterials,
    filter(material => {
      const hasActiveType = propSatisfies(
        includes(__, activeItems),
        "materialType"
      );
      return hasActiveType(material);
    })
  );

export const selectMaterialsByPage = pageNumber =>
  createSelector(selectMaterials, materials => {
    const pageNumberFilter = propEq("page", pageNumber);

    return materials
      .filter(({ positions }) => positions.some(pageNumberFilter))
      .map(material => {
        const positions = material.positions.filter(pageNumberFilter);

        return {
          ...material,
          positions
        };
      });
  });

export const selectMaterialsGroupedByPage = createSelector(
  selectMaterials,
  materials => {
    const singlePositionMaterials = reduce(
      (acc, { positions = [], ...material }) =>
        concat(
          acc,
          map(position => merge(material, { position }), positions)
        ),
      [],
      materials
    );

    const groupedByPage = groupBy(
      pathOr(NON_ASSIGNED_PAGE, ["position", "page"]),
      singlePositionMaterials
    );

    return groupedByPage;
  }
);

export const selectMaterialGroupsByPages = pages =>
  createSelector(selectMaterialsGroupedByPage, group => {
    if (isEmpty(pages)) {
      return group;
    }

    return pick(pages, group);
  });

export const selectMaterialGroupsByPagesByTypes = curryN(2, (pages, types) =>
  createSelector(
    selectMaterialGroupsByPages(pages),
    selectMarkedMaterialsByPages,
    selectIsPresentationActive,
    selectIsPresentationMaterialVisible,
    (
      group,
      markedMaterials,
      isPresentationMode,
      isPresentationMaterialVisible
    ) => {
      const pickByTypes = filter(
        propSatisfies(includes(__, types), "materialType")
      );

      let filteredGroups = map(pickByTypes, group);

      if (isPresentationMode && isPresentationMaterialVisible) {
        // FIXME: omit unassigned page materials earlier
        let filteredMarkedMaterials = omit([-2], markedMaterials);

        filteredMarkedMaterials = map(
          map(material => {
            return {
              ...material,
              position: {
                isPresentationOnly: true
              }
            };
          }),
          filteredMarkedMaterials
        );

        filteredGroups = mergeWith(
          concat,
          filteredGroups,
          filteredMarkedMaterials
        );
      }

      return reject(isEmpty, filteredGroups);
    }
  )
);

export const selectMaterialsByPagesByTypesSorted = pages => types =>
  createSelector(
    selectMaterialGroupsByPagesByTypesSorted(pages)(types),
    materials => Object.values(materials).flat()
  );

export const selectMaterialGroupsByPagesByTypesSorted = pages => types =>
  createSelector(
    selectMaterialGroupsByPagesByTypes(pages)(types),
    pipe(
      map(
        sort(
          variadicEither([
            yPositionComparator,
            xPositionComparator,
            titleComparator
          ])
        )
      ),
      map(sort(typeComparator))
    )
  );

export const selectTypeSizesByPages = pages =>
  createSelector(
    [selectMaterialGroupsByPages(pages), selectPagelessMaterials],
    (group, pagelessMaterials) => {
      let sizes = objFromKeys(MATERIAL_TYPES, 0);
      const flattened = unnest(map(values, values(group)));

      flattened.forEach(material => {
        sizes = over(
          lensProp(prop("materialType", material)),
          value => (material.notInSidebar ? value : inc(value)),
          sizes
        );
      });

      pagelessMaterials.forEach(material => {
        sizes = over(
          lensProp(prop("materialType", material)),
          value => (material.notInSidebar ? value : inc(value)),
          sizes
        );
      });

      return sizes;
    }
  );

export const selectMaterialById = id =>
  createSelector(selectMaterials, find(propEq("_id", id)));

const initialState = () => [];

export default function reducer(state = initialState(), action = {}) {
  switch (action.type) {
    case ADD_MATERIAL:
      return [...state, action.payload];
    case ADD_MATERIAL_MULTIPLE:
      return union(state, action.payload);
    case REMOVE_MATERIAL:
      return state.filter(material => material._id !== action.payload);
    case REMOVE_MATERIAL_POSITION:
      return state.map(material => {
        const positions = material.positions.filter(
          position => position._id !== action.payload
        );

        return { ...material, positions };
      });
    case UPDATE_MATERIAL:
      return state.map(material => {
        if (material._id === action.payload._id) {
          return {
            ...material,
            ...action.payload,
            _rev: action.payload._rev || material._rev
          };
        } else {
          return material;
        }
      });
    case UPDATE_MATERIAL_POSITIONS:
      return state.map(material => {
        if (material._id === action.payload.materialId) {
          let positions = [];
          const newPosition = {
            ...action.payload.newPosition,
            page: material.withOffset
              ? absolutePageToRelativeNumerical(action.payload.newPosition.page)
              : action.payload.newPosition.page
          };
          if (material.positions === undefined) {
            positions.push(newPosition);
          } else {
            positions = [...material.positions, newPosition];
          }

          return { ...material, positions };
        }

        return material;
      });
    case MOVE_MATERIAL:
      return state.map(material => {
        if (material._id === action.payload.materialId) {
          const positions = material.positions.map(position => {
            if (position._id === action.payload.positionId) {
              return {
                ...position,
                ...action.payload.position,
                page: material.withOffset
                  ? absolutePageToRelativeNumerical(
                      action.payload.position.page
                    )
                  : action.payload.position.page
              };
            }

            return position;
          });

          return { ...material, positions };
        }

        return material;
      });
    default:
      return state;
  }
}
